import { usePhysicsStore } from "../stores/physicsStore";
import { getSceneState } from "../stores/sceneStore";
import { stopActiveServoSession } from "../ik/ikTarget/servoSession";
import type { BehaviorDocument, BTNode, BTNodeId } from "./types";
import { dispatchLeaf, type LeafContext, type LeafMemory, type TickResult } from "./actions";

export type { TickResult } from "./actions";

export type NodeStatus = TickResult | "idle";

export type BehaviorBlackboard = {
  episodeOutcome: "success" | "failure" | null;
};

export type RunnerMemory = {
  /** sequence/selector: current child index */
  compositeIndex: Map<BTNodeId, number>;
  /** retry: attempts used for current child */
  retryCount: Map<BTNodeId, number>;
  /** repeat: successes completed */
  repeatDone: Map<BTNodeId, number>;
  leaf: Map<BTNodeId, LeafMemory>;
  /** Most recent tick result per node (for UI badges). */
  statuses: Map<BTNodeId, TickResult>;
};

function isConditionLeafKind(kind: BTNode["kind"]): boolean {
  return (
    kind === "conditionObjectGrasped" ||
    kind === "conditionEeNear" ||
    kind === "conditionObjectNear" ||
    kind === "conditionTimeElapsed"
  );
}

function emptyMemory(): RunnerMemory {
  return {
    compositeIndex: new Map(),
    retryCount: new Map(),
    repeatDone: new Map(),
    leaf: new Map(),
    statuses: new Map(),
  };
}

export function createBehaviorRunner() {
  let mem = emptyMemory();
  let doc: BehaviorDocument | null = null;
  let bb: BehaviorBlackboard = { episodeOutcome: null };
  let activeNodeId: BTNodeId | null = null;
  let lastFailureReason: string | null = null;

  const resetMemory = () => {
    mem = emptyMemory();
    bb = { episodeOutcome: null };
    activeNodeId = null;
    lastFailureReason = null;
  };

  const getLeafMem = (id: BTNodeId): LeafMemory => {
    let m = mem.leaf.get(id);
    if (!m) {
      m = {};
      mem.leaf.set(id, m);
    }
    return m;
  };

  const buildLeafContext = (): LeafContext => ({
    doc: doc as BehaviorDocument,
    scene: getSceneState(),
    physicsOk: usePhysicsStore.getState().isRunning === true,
    getLeafMem,
    clearLeafMem: (id) => {
      mem.leaf.delete(id);
    },
    setActiveNode: (id) => {
      activeNodeId = id;
    },
    setOutcome: (o) => {
      bb.episodeOutcome = o;
    },
    setFailureReason: (message) => {
      if (!lastFailureReason) lastFailureReason = message;
    },
  });

  const tickNode = (id: BTNodeId): TickResult => {
    if (!doc) {
      if (!lastFailureReason) lastFailureReason = "Behavior document is not loaded.";
      return "failure";
    }
    const node = doc.nodes[id];
    if (!node) {
      if (!lastFailureReason) lastFailureReason = `Node "${id}" is missing from the document.`;
      return "failure";
    }

    let r: TickResult;
    if (
      node.kind === "sequence" ||
      node.kind === "selector" ||
      node.kind === "retry" ||
      node.kind === "repeat" ||
      node.kind === "ifThenElse"
    ) {
      r = tickComposite(node);
    } else {
      r = dispatchLeaf(buildLeafContext(), node);
    }
    mem.statuses.set(id, r);
    if (r === "failure" && !lastFailureReason) {
      const name = node.label?.trim() || node.kind;
      lastFailureReason = `Step failed: ${name}`;
    }
    return r;
  };

  const tickComposite = (node: BTNode): TickResult => {
    if (!doc || !("kind" in node)) return "failure";
    if (node.kind === "sequence") {
      let idx = mem.compositeIndex.get(node.id) ?? 0;
      while (idx < node.children.length) {
        const r = tickNode(node.children[idx]);
        if (r === "running") {
          mem.compositeIndex.set(node.id, idx);
          return "running";
        }
        if (r === "failure") {
          if (!lastFailureReason) {
            const child = doc?.nodes[node.children[idx]];
            const label = child?.label?.trim() || child?.kind || `child ${idx + 1}`;
            lastFailureReason = `Sequence failed at step ${idx + 1}: ${label}.`;
          }
          mem.compositeIndex.delete(node.id);
          return "failure";
        }
        idx += 1;
        mem.compositeIndex.set(node.id, idx);
      }
      mem.compositeIndex.delete(node.id);
      return "success";
    }
    if (node.kind === "selector") {
      let idx = mem.compositeIndex.get(node.id) ?? 0;
      while (idx < node.children.length) {
        const r = tickNode(node.children[idx]);
        if (r === "running") {
          mem.compositeIndex.set(node.id, idx);
          return "running";
        }
        if (r === "success") {
          mem.compositeIndex.delete(node.id);
          return "success";
        }
        idx += 1;
        mem.compositeIndex.set(node.id, idx);
      }
      mem.compositeIndex.delete(node.id);
      if (!lastFailureReason) {
        lastFailureReason = "Fallback failed: every child branch returned failure.";
      }
      return "failure";
    }
    if (node.kind === "retry") {
      const childId = node.child;
      if (!childId) return "success";
      let attempts = mem.retryCount.get(node.id) ?? 0;
      const max = Math.max(1, Math.floor(node.maxAttempts));
      while (attempts < max) {
        const r = tickNode(childId);
        if (r === "running") {
          mem.retryCount.set(node.id, attempts);
          return "running";
        }
        if (r === "success") {
          mem.retryCount.delete(node.id);
          return "success";
        }
        attempts += 1;
        mem.compositeIndex.delete(childId);
        mem.leaf.delete(childId);
      }
      mem.retryCount.delete(node.id);
      if (!lastFailureReason) {
        lastFailureReason = `Retry failed after ${max} attempts.`;
      }
      return "failure";
    }
    if (node.kind === "repeat") {
      const childId = node.child;
      if (!childId) return "success";
      const maxRep = Math.min(10_000, Math.max(1, Math.floor(node.count)));
      let done = mem.repeatDone.get(node.id) ?? 0;
      while (done < maxRep) {
        const r = tickNode(childId);
        if (r === "running") {
          mem.repeatDone.set(node.id, done);
          return "running";
        }
        if (r === "failure") {
          if (!lastFailureReason) {
            lastFailureReason = `Repeat failed on iteration ${done + 1} of ${maxRep}.`;
          }
          mem.repeatDone.delete(node.id);
          return "failure";
        }
        done += 1;
        mem.compositeIndex.delete(childId);
        mem.leaf.delete(childId);
      }
      mem.repeatDone.delete(node.id);
      return "success";
    }
    if (node.kind === "ifThenElse") {
      const phase = mem.compositeIndex.get(node.id) ?? 0;
      if (phase === 1) {
        if (!node.thenChild || !doc?.nodes[node.thenChild]) {
          mem.compositeIndex.delete(node.id);
          return "failure";
        }
        const r = tickNode(node.thenChild);
        if (r === "running") return "running";
        mem.compositeIndex.delete(node.id);
        return r;
      }
      if (phase === 2) {
        if (!node.elseChild || !doc?.nodes[node.elseChild]) {
          mem.compositeIndex.delete(node.id);
          return "failure";
        }
        const r = tickNode(node.elseChild);
        if (r === "running") return "running";
        mem.compositeIndex.delete(node.id);
        return r;
      }

      const conditionId = node.conditionChild;
      if (!conditionId || !doc?.nodes[conditionId]) {
        if (!lastFailureReason) {
          lastFailureReason = "If / Then / Else failed: missing condition child.";
        }
        mem.compositeIndex.delete(node.id);
        return "failure";
      }
      const conditionNode = doc.nodes[conditionId];
      if (!isConditionLeafKind(conditionNode.kind)) {
        if (!lastFailureReason) {
          lastFailureReason =
            "If / Then / Else failed: condition slot must contain a condition node.";
        }
        mem.compositeIndex.delete(node.id);
        return "failure";
      }
      const cond = tickNode(conditionId);
      if (cond === "running") return "running";

      if (cond === "success") {
        if (!node.thenChild || !doc?.nodes[node.thenChild]) {
          if (!lastFailureReason) {
            lastFailureReason = "If / Then / Else failed: missing Then branch.";
          }
          mem.compositeIndex.delete(node.id);
          return "failure";
        }
        const thenResult = tickNode(node.thenChild);
        if (thenResult === "running") {
          mem.compositeIndex.set(node.id, 1);
          return "running";
        }
        mem.compositeIndex.delete(node.id);
        return thenResult;
      }

      if (!node.elseChild || !doc?.nodes[node.elseChild]) {
        if (!lastFailureReason) {
          lastFailureReason =
            "If / Then / Else failed: condition was false and Else branch is missing.";
        }
        mem.compositeIndex.delete(node.id);
        return "failure";
      }
      const elseResult = tickNode(node.elseChild);
      if (elseResult === "running") {
        mem.compositeIndex.set(node.id, 2);
        return "running";
      }
      mem.compositeIndex.delete(node.id);
      return elseResult;
    }
    return "failure";
  };

  const snapshotStatuses = (): Record<BTNodeId, TickResult> => {
    const out: Record<BTNodeId, TickResult> = {};
    mem.statuses.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  };

  return {
    startRun(nextDoc: BehaviorDocument) {
      doc = nextDoc;
      resetMemory();
      stopActiveServoSession();
    },
    stopRun() {
      stopActiveServoSession();
      doc = null;
      resetMemory();
    },
    tick(): {
      result: TickResult;
      activeNodeId: BTNodeId | null;
      blackboard: BehaviorBlackboard;
      statuses: Record<BTNodeId, TickResult>;
      failureReason: string | null;
    } {
      if (!doc) {
        return {
          result: "failure",
          activeNodeId: null,
          blackboard: bb,
          statuses: {},
          failureReason: "Behavior document is not loaded.",
        };
      }
      if (usePhysicsStore.getState().isRunning !== true) {
        stopActiveServoSession();
        return {
          result: "failure",
          activeNodeId: null,
          blackboard: bb,
          statuses: snapshotStatuses(),
          failureReason: "Physics is not running.",
        };
      }
      lastFailureReason = null;
      activeNodeId = null;
      const r = tickNode(doc.rootId);
      return {
        result: r,
        activeNodeId,
        blackboard: bb,
        statuses: snapshotStatuses(),
        failureReason: r === "failure" ? lastFailureReason : null,
      };
    },
    getBlackboard: () => bb,
    peekDoc: () => doc,
  };
}
